import React, { MutableRefObject, useContext, useRef } from 'react'
import AddIcon from '@mui/icons-material/Add';
import { useTranslation } from 'react-i18next';
import UserContext from '../contexts/UserContext';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useParams } from 'react-router-dom';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import { QRCode } from 'react-qrcode-logo';
import { primary } from '../styles/colors';
import PrimaryButton from '../buttons/PrimaryButton';
import SaveIcon from '@mui/icons-material/Save';
import AutoTranslateButton from './AutoTranslateButton';
import MenuHeaderEditorButton from './MenuHeaderEditorButton';
import MenuImportButton from './MenuImportButton';

type Props = {
    onSave: () => void;
    onNewCategoryClick: () => void;
    reloadMenu?: () => void;
}

const MenuEditorHeader = (props: Props) => {
    const { t } = useTranslation();
    const { selectedRestaurant } = useContext(UserContext);
    const params = useParams();
    const qrRef = useRef<QRCode>();

    function getMenuUrl() {
        return `${process.env.REACT_APP_FRONTEND_URL}/menu/${selectedRestaurant?.restaurantShortName}/${params.lang || "hu"}`;
    }

    function handleMenuButtonClick() {
        if (selectedRestaurant == null) return;
        window.open(getMenuUrl(), '_blank');
    }

    function handleQRButtonClick() {
        qrRef.current?.download('png', `${selectedRestaurant?.restaurantShortName}-menu-qr-code.png`);
    }

    return (
        <>
            <div className="pr-2">
                <div className="pt-4">
                    <div className="flex flex-wrap gap-3">
                        <div className="grow flex flex-wrap gap-3" >
                            <div>
                                <PrimaryButton
                                    variant="outlined"
                                    onClick={handleMenuButtonClick}
                                    startIcon={<OpenInNewIcon />}
                                >
                                    {t("menu.menu")}
                                </PrimaryButton>
                            </div>
                            <div>
                                <PrimaryButton
                                    variant="outlined"
                                    onClick={handleQRButtonClick}
                                    startIcon={<QrCode2Icon />}
                                >
                                    {t("menu.generate_qr")}
                                </PrimaryButton>
                                <QRCode
                                    style={{ display: "none" }}
                                    ref={qrRef as MutableRefObject<QRCode>}
                                    value={getMenuUrl()}
                                    ecLevel='H'
                                    size={300}
                                    fgColor={primary.dark}
                                    logoImage='../assets/logo_green.svg'
                                    logoPadding={8}
                                    logoPaddingStyle='circle'
                                    removeQrCodeBehindLogo={true}
                                    qrStyle='squares'
                                />
                            </div>
                            <div>
                                <AutoTranslateButton
                                    restaurantShortName={selectedRestaurant?.restaurantShortName}
                                    reloadMenu={props.reloadMenu}
                                />
                            </div>
                            <div>
                                <MenuHeaderEditorButton
                                    restaurantShortName={selectedRestaurant?.restaurantShortName}
                                />
                            </div>
                            <div>
                                <MenuImportButton
                                    onStatusChange={props.reloadMenu}
                                />
                            </div>
                        </div>
                        <div>
                            <PrimaryButton
                                onClick={props.onSave}
                                startIcon={<SaveIcon />}
                            >
                                {t("common.save")}
                            </PrimaryButton>
                        </div>
                        <div>
                            <PrimaryButton
                                variant="strike"
                                onClick={props.onNewCategoryClick}
                                startIcon={<AddIcon />}
                            >
                                {t("menu.new_category")}
                            </PrimaryButton>
                        </div>
                    </div>
                </div>

                <div className="mt-2">
                    <div
                        className="bg-primary-light h-[10px] rounded-t-[10px] w-full"
                    />
                </div>
            </div>
        </>
    )
}

export default MenuEditorHeader